import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import Toast, { ToastMessage } from './Toast';
import './ToastContainer.css';

type ShowToastFn = (toast: Omit<ToastMessage, 'id'>) => void;

interface ToastContextType {
  showToast: ShowToastFn;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Imperative API for non-React code.
let imperativeShowToast: ShowToastFn | null = null;
export const toast: ShowToastFn = (t) => {
  if (imperativeShowToast) imperativeShowToast(t);
  else console.warn('toast() called before ToastProvider mounted:', t);
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36);
    const newToast: ToastMessage = {
      ...toast,
      id,
    };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    imperativeShowToast = showToast;
    return () => {
      imperativeShowToast = null;
    };
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <Toast key={toast.id} message={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

